import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useToast } from '../hooks/use-toast';
import { CheckCircle2, AlertTriangle, CalendarX } from 'lucide-react';
import ModalNav from './ModalNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Date limite : 10 août 2026 à 23h59
const RSVP_DEADLINE = new Date('2026-08-10T23:59:59').getTime();
const RSVP_DEADLINE_LABEL = '10 août 2026';

const RSVPContent = ({ onClose }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    attending: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isDeadlinePassed = Date.now() > RSVP_DEADLINE;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAttendingChange = (value) => {
    setFormData(prev => ({ ...prev, attending: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isDeadlinePassed) {
      toast({
        title: 'Inscriptions fermées',
        description: `La date limite (${RSVP_DEADLINE_LABEL}) est dépassée.`,
        variant: 'destructive',
      });
      return;
    }

    if (!formData.name || !formData.attending || !formData.message.trim()) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive"
      });
      return;
    }

    if (formData.attending === 'yes' && !formData.email.trim()) {
      toast({
        title: "Email requis",
        description: "Indiquez votre email pour recevoir la confirmation",
        variant: "destructive"
      });
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/rsvp`, formData);
      setSubmitted(true);
      toast({
        title: "Réponse enregistrée !",
        description: formData.attending === 'yes' ? "On vous attend avec impatience !" : "Dommage que vous ne puissiez pas venir...",
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "Erreur d'envoi",
        description: "Impossible d'enregistrer votre réponse. Réessayez dans un instant.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setFormData({ name: '', email: '', attending: '', message: '' });
  };

  if (submitted) {
    return (
      <div>
        <ModalNav active="rsvp" />
        <div className="py-10 px-6 md:px-10 text-center">
        <CheckCircle2 className="w-20 h-20 text-orange-500 mx-auto mb-6" />
        <h2 className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-500 mb-4">
          Merci {formData.name} !
        </h2>
        <p className="text-lg text-gray-300 mb-8">
          {formData.attending === 'yes'
            ? "Votre présence est confirmée, merci d'arriver à l'heure."
            : "Merci d'avoir répondu. Nous avons bien pris note de votre absence."}
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Button
            onClick={handleReset}
            data-testid="rsvp-reset-btn"
            className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-bold"
          >
            Modifier ma réponse
          </Button>
          {onClose && (
            <Button
              onClick={onClose}
              variant="outline"
              data-testid="rsvp-close-btn"
              className="border-orange-600 text-orange-400 hover:bg-orange-600 hover:text-white"
            >
              Fermer
            </Button>
          )}
        </div>
        </div>
      </div>
    );
  }

  if (isDeadlinePassed) {
    return (
      <div>
        <ModalNav active="rsvp" />
        <div className="py-10 px-6 md:px-10 text-center">
          <CalendarX className="w-20 h-20 text-orange-500/70 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-500 mb-4">
            Inscriptions fermées
          </h2>
          <p className="text-lg text-gray-300 max-w-xl mx-auto mb-6">
            La date limite pour confirmer sa présence était le <span className="text-orange-400 font-bold">{RSVP_DEADLINE_LABEL}</span>.
          </p>
          <p className="text-base text-gray-400 max-w-xl mx-auto">
            Si vous avez un imprévu, contactez directement Rose pour en discuter.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ModalNav active="rsvp" />
      <div className="py-10 px-6 md:px-10">
      <div className="text-center mb-8">
        <h2 className="text-4xl md:text-5xl font-black mb-3 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-500 to-orange-600">
          Confirmez votre présence
        </h2>
        <div className="mt-4 inline-flex items-center gap-2 bg-orange-600/15 border border-orange-500/40 px-4 py-2 rounded-full text-sm text-orange-300">
          <AlertTriangle className="w-4 h-4" />
          <span>Date limite pour répondre : <span className="font-bold text-orange-200">{RSVP_DEADLINE_LABEL}</span></span>
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        <div>
          <Label htmlFor="name" className="text-orange-400 font-bold mb-2 block">Nom complet *</Label>
          <Input
            id="name"
            name="name"
            data-testid="rsvp-name-input"
            value={formData.name}
            onChange={handleInputChange}
            className="bg-black/60 border-orange-600/50 text-white focus:border-orange-500"
            required
          />
        </div>

        <div>
          <Label className="text-orange-400 font-bold mb-3 block">Serez-vous présent(e) ? *</Label>
          <RadioGroup value={formData.attending} onValueChange={handleAttendingChange}>
            <div className="flex items-center space-x-3 mb-3 bg-black/40 p-4 rounded-lg border border-orange-600/30 hover:border-orange-500 transition-colors">
              <RadioGroupItem value="yes" id="yes" data-testid="rsvp-yes-radio" className="border-orange-500 text-orange-500" />
              <Label htmlFor="yes" className="text-white text-lg cursor-pointer flex-1">Oui, je serai là ! 🔥</Label>
            </div>
            <div className="flex items-center space-x-3 bg-black/40 p-4 rounded-lg border border-orange-600/30 hover:border-orange-500 transition-colors">
              <RadioGroupItem value="no" id="no" data-testid="rsvp-no-radio" className="border-orange-500 text-orange-500" />
              <Label htmlFor="no" className="text-white text-lg cursor-pointer flex-1">Non, je ne pourrai pas venir</Label>
            </div>
          </RadioGroup>
        </div>

        {formData.attending === 'yes' && (
          <div>
            <Label htmlFor="email" className="text-orange-400 font-bold mb-2 block">Email * <span className="text-xs text-gray-400 font-normal">(pour recevoir la confirmation)</span></Label>
            <Input
              id="email"
              name="email"
              type="email"
              data-testid="rsvp-email-input"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="votre@email.com"
              className="bg-black/60 border-orange-600/50 text-white focus:border-orange-500"
              required
            />
          </div>
        )}

        <div>
          <Label htmlFor="message" className="text-orange-400 font-bold mb-2 block">Message *</Label>
          <Textarea
            id="message"
            name="message"
            data-testid="rsvp-message-textarea"
            value={formData.message}
            onChange={handleInputChange}
            rows={3}
            className="bg-black/60 border-orange-600/50 text-white focus:border-orange-500 resize-none"
            placeholder=""
            required
          />
        </div>

        <Button
          type="submit"
          size="lg"
          disabled={submitting}
          data-testid="rsvp-submit-btn"
          className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-bold text-lg py-6 border-2 border-orange-400 shadow-lg shadow-orange-500/50 transition-all duration-300 hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? 'Envoi en cours...' : 'Envoyer ma réponse'}
        </Button>
      </form>
      </div>
    </div>
  );
};

export default RSVPContent;
